import type { I18n } from "@lingui/core";
import { formatDateLong } from "@/lib/format";

/**
 * Calculates total months between a birthdate and a reference date (defaults to today).
 * Returns null if the date is invalid or if the birthdate is after the reference date.
 */
function calculateTotalMonths(birthDate: string, referenceDate?: Date): number | null {
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return null;

  const ref = referenceDate ?? new Date();
  if (date > ref) return null;

  let totalMonths =
    (ref.getFullYear() - date.getFullYear()) * 12 +
    (ref.getMonth() - date.getMonth());

  if (ref.getDate() < date.getDate()) {
    totalMonths -= 1;
  }

  return Math.max(totalMonths, 0);
}

/**
 * Formats a child's age using the 18-month threshold rule:
 * - 0 months: "Newborn"
 * - 1 month: "1 month"
 * - 2-18 months: "{N} months"
 * - >18 months with 0 remainder: "{Y} years" / "1 year"
 * - >18 months with remainder: "{Y} years, {M} months" (with singular/plural)
 *
 * Returns null if birthdate is missing, invalid, or in the future.
 */
export function formatChildAge(
  birthDate: string | undefined,
  i18n: I18n,
  referenceDate?: Date
): string | null {
  if (!birthDate) return null;

  const totalMonths = calculateTotalMonths(birthDate, referenceDate);
  if (totalMonths === null) return null;

  if (totalMonths === 0) {
    return i18n._({ id: "common.age.newborn", message: "Newborn" });
  }

  if (totalMonths <= 18) {
    if (totalMonths === 1) {
      return i18n._({ id: "common.age.oneMonth", message: "1 month" });
    }
    return i18n._({
      id: "common.age.monthsOld",
      message: "{count} months",
      values: { count: totalMonths },
    });
  }

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (months === 0) {
    if (years === 1) {
      return i18n._({ id: "common.age.oneYear", message: "1 year" });
    }
    return i18n._({
      id: "common.age.yearsOld",
      message: "{count} years",
      values: { count: years },
    });
  }

  if (years === 1) {
    return i18n._({
      id: "common.age.oneYearAndMonths",
      message: "1 year, {months} months",
      values: { months },
    });
  }
  if (months === 1) {
    return i18n._({
      id: "common.age.yearsAndOneMonth",
      message: "{years} years, 1 month",
      values: { years },
    });
  }
  return i18n._({
    id: "common.age.yearsAndMonths",
    message: "{years} years, {months} months",
    values: { years, months },
  });
}

/**
 * Formats a birthdate string with age label prepended.
 * Returns "{age} - {formattedDate}" or just the formatted date if no age can be calculated.
 * Returns empty string if birthdate is missing or invalid.
 */
export function formatBirthDateWithAge(
  birthDate: string | undefined,
  locale: string,
  i18n: I18n,
  referenceDate?: Date
): string {
  if (!birthDate) return "";

  if (Number.isNaN(new Date(birthDate).getTime())) return "";
  const formattedDate = formatDateLong(birthDate, locale);
  if (!formattedDate) return "";

  const age = formatChildAge(birthDate, i18n, referenceDate);
  if (!age) return formattedDate;

  return `${age} \u2022 ${formattedDate}`;
}
